<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'

const props = withDefaults(defineProps<{
  defaultHeight?: string
  submitOnEnter?: boolean
}>(), {
  submitOnEnter: true,
})

const events = defineEmits<{
  (event: 'submit', message: string): void
  (event: 'pasteFile', files: File[]): void
}>()

const input = defineModel<string>({
  default: '',
})

const textareaRef = ref<HTMLTextAreaElement>()
const textareaHeight = ref('auto')
let resizeFrame: number | null = null
let lastWidth = 0

function resizeTextarea() {
  if (resizeFrame !== null)
    return

  resizeFrame = requestAnimationFrame(() => {
    resizeFrame = null
    const textarea = textareaRef.value
    if (!textarea)
      return

    // 直接写入 DOM，确保下面读取 scrollHeight 时已经使用了收缩后的高度。
    textarea.style.height = 'auto'
    const value = textarea.value
    if (value === '') {
      const nextHeight = props.defaultHeight || 'fit-content'
      textarea.style.height = nextHeight
      textareaHeight.value = nextHeight
      return
    }

    // scrollHeight 会随宽度换行变化，重新测量可避免横屏/窗口缩放后高度残留。
    const nextHeight = `${textarea.scrollHeight + 4}px`
    textarea.style.height = nextHeight
    textareaHeight.value = nextHeight
  })
}

let textareaResizeObserver: ResizeObserver | null = null

// 与上游差异：恢复被上游重构丢掉的 focus 暴露。下游（stage-web MobileInteractiveArea）
// 依赖 BasicTextarea 实例的 focus() 在 AI 回复后重新聚焦输入框，去掉会导致 TS2339。
function focus() {
  textareaRef.value?.focus()
}
defineExpose({ focus })

function onKeyDown(e: KeyboardEvent) {
  if (!props.submitOnEnter)
    return

  if (e.code === 'Enter' && !e.shiftKey) { // just block Enter is enough, Shift+Enter by default generates a newline
    e.preventDefault()
    events('submit', input.value)
  }
}

function onPaste(e: ClipboardEvent) {
  if (!e.clipboardData)
    return

  const { files } = e.clipboardData
  if (files.length > 0) {
    e.preventDefault()
    events('pasteFile', Array.from(files))
  }
}

// javascript - Creating a textarea with auto-resize - Stack Overflow
// https://stackoverflow.com/questions/454202/creating-a-textarea-with-auto-resize
watch(input, resizeTextarea, { immediate: true, flush: 'post' })

onMounted(() => {
  const textarea = textareaRef.value
  if (!textarea)
    return

  lastWidth = textarea.clientWidth
  textareaResizeObserver = new ResizeObserver(([entry]) => {
    const width = entry?.contentRect.width ?? textarea.clientWidth
    if (Math.abs(width - lastWidth) < 0.5)
      return
    lastWidth = width
    resizeTextarea()
  })
  textareaResizeObserver.observe(textarea)
})

onUnmounted(() => {
  textareaResizeObserver?.disconnect()
  textareaResizeObserver = null
  if (resizeFrame !== null) {
    cancelAnimationFrame(resizeFrame)
    resizeFrame = null
  }
})
</script>

<template>
  <textarea
    ref="textareaRef"
    v-model="input"
    rows="1"
    wrap="soft"
    :style="{ height: textareaHeight }"
    @input="resizeTextarea"
    @keydown="onKeyDown"
    @paste="onPaste"
  />
</template>
